import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';
import { Calendar as CalendarIcon, Scissors, MapPin, Phone, Clock, Check, ArrowLeft, User, MessageSquare, AlertTriangle, MessageCircle } from 'lucide-react';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Logos y configuración de negocios
const BUSINESSES = {
  'The Holland Barbershop': {
    logo: 'https://customer-assets.emergentagent.com/job_cutsync-2/artifacts/sfpd5g0k_The%20Holland.jpeg',
    name: 'The Holland Barbershop',
    color: 'gold',
    bgGradient: 'from-amber-900/30 via-black/50 to-[#050505]',
  },
  'Paulo Cisneros': {
    logo: 'https://customer-assets.emergentagent.com/job_cutsync-2/artifacts/7hd0na99_Paulo%20Cisneros.jpeg',
    name: 'Paulo Cisneros',
    color: 'blue',
    bgGradient: 'from-blue-900/30 via-black/50 to-[#050505]',
  },
};

// Generate time slots from opening to closing
const generateTimeSlots = (openingTime = '09:00', closingTime = '20:00') => {
  if (!openingTime || !closingTime) return [];
  
  const slots = [];
  const [openHour, openMin] = openingTime.split(':').map(Number);
  const [closeHour, closeMin] = closingTime.split(':').map(Number);
  
  for (let hour = openHour; hour < closeHour; hour++) {
    slots.push(`${hour.toString().padStart(2, '0')}:00`);
    slots.push(`${hour.toString().padStart(2, '0')}:30`);
  }
  // Add final slot if closing time allows
  if (closeMin >= 30) {
    slots.push(`${closeHour.toString().padStart(2, '0')}:00`);
  }
  return slots;
};

// Get time slots based on day of week
const getTimeSlotsForDate = (branch, date) => {
  if (!branch || !date) return generateTimeSlots();
  
  const dayOfWeek = date.getDay(); // 0 = Sunday, 6 = Saturday
  
  if (dayOfWeek === 0) { // Sunday
    if (!branch.sunday_open) return []; // Closed
    return generateTimeSlots(
      branch.sunday_opening_time || branch.opening_time,
      branch.sunday_closing_time || branch.closing_time
    );
  }
  
  if (dayOfWeek === 6) { // Saturday
    if (branch.saturday_open === false) return []; // Closed
    return generateTimeSlots(
      branch.saturday_opening_time || branch.opening_time,
      branch.saturday_closing_time || branch.closing_time
    );
  }
  
  // Regular weekday
  return generateTimeSlots(branch.opening_time, branch.closing_time);
};

// Check if branch is open on a specific date
const isBranchOpenOnDate = (branch, date) => {
  if (!branch || !date) return true;
  
  const dayOfWeek = date.getDay();
  
  if (dayOfWeek === 0) return branch.sunday_open === true;
  if (dayOfWeek === 6) return branch.saturday_open !== false;
  
  return true; // Weekdays always open
};

export default function BookingLanding() {
  const [branches, setBranches] = useState([]);
  const [barbers, setBarbers] = useState([]);
  const [services, setServices] = useState([]);
  const [selectedBusiness, setSelectedBusiness] = useState(null);
  const [selectedBranch, setSelectedBranch] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [appointmentBooked, setAppointmentBooked] = useState(null);
  const [bookedSlots, setBookedSlots] = useState([]); // Horarios ocupados por el barbero seleccionado
  const [holidayMode, setHolidayMode] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    clientName: '',
    clientWhatsapp: '',
    date: null,
    time: '',
    barberId: '',
    serviceId: '',
    notes: ''
  });
  const [calendarOpen, setCalendarOpen] = useState(false);

  useEffect(() => {
    fetchBranches();
    fetchHolidayMode();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchHolidayMode = async () => {
    try {
      const response = await axios.get(`${API}/settings/holiday-mode`);
      setHolidayMode(response.data);
    } catch (error) {
      // Silent fail - holiday mode defaults to disabled
    }
  };

  useEffect(() => {
    if (selectedBranch) {
      // Fetch barbers filtered by selected date (to exclude those on rest day)
      const dateParam = formData.date ? format(formData.date, 'yyyy-MM-dd') : null;
      fetchBarbers(selectedBranch, dateParam);
      // Fetch services for this branch
      fetchServices(selectedBranch);
    }
  }, [selectedBranch, formData.date]);

  // Fetch booked slots when date or barber changes
  useEffect(() => {
    if (selectedBranch && formData.date && formData.barberId) {
      fetchBookedSlots(selectedBranch, format(formData.date, 'yyyy-MM-dd'), formData.barberId);
    } else {
      setBookedSlots([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBranch, formData.date, formData.barberId]);

  const fetchBranches = async () => {
    try {
      const response = await axios.get(`${API}/branches/public`);
      setBranches(response.data);
    } catch (error) {
      // error handled
    }
  };

  const fetchBarbers = async (branchId, dateStr = null) => {
    try {
      const params = dateStr ? `?date=${dateStr}` : '';
      const response = await axios.get(`${API}/barbers/branch/${branchId}/public${params}`);
      setBarbers(response.data);
      // Clear selected barber if they're no longer available (on rest day)
      if (formData.barberId && !response.data.find(b => b.id === formData.barberId)) {
        setFormData(prev => ({...prev, barberId: ''}));
      }
    } catch (error) {
      setBarbers([]);
    }
  };

  const fetchBookedSlots = async (branchId, dateStr, barberId) => {
    try {
      const response = await axios.get(
        `${API}/appointments/public/booked-slots?branch_id=${branchId}&date=${dateStr}&barber_id=${barberId}`
      );
      setBookedSlots(response.data.booked_times || []);
      // Clear selected time if it's now booked
      if (formData.time && response.data.booked_times?.includes(formData.time)) {
        setFormData(prev => ({...prev, time: ''}));
        toast.info('El horario seleccionado ya no está disponible');
      }
    } catch (error) {
      setBookedSlots([]);
    }
  };

  const fetchServices = async (branchId = null) => {
    try {
      const endpoint = branchId 
        ? `${API}/services/public/${branchId}`
        : `${API}/services/public`;
      const response = await axios.get(endpoint);
      setServices(response.data);
      // Clear selected service if it's no longer available
      if (formData.serviceId && !response.data.find(s => s.id === formData.serviceId)) {
        setFormData(prev => ({...prev, serviceId: ''}));
      }
    } catch (error) {
      setServices([]);
    }
  };

  const selectBusiness = (businessName) => {
    setSelectedBusiness(businessName);
    setSelectedBranch('');
    setShowForm(false);
    setAppointmentBooked(null);
    resetForm();
  };

  const goBackToBusinessSelection = () => {
    setSelectedBusiness(null);
    setSelectedBranch('');
    setShowForm(false);
    setAppointmentBooked(null);
    resetForm();
  };

  const resetForm = () => {
    setFormData({
      clientName: '',
      clientWhatsapp: '',
      date: null,
      time: '',
      barberId: '',
      serviceId: '',
      notes: ''
    });
  };

  // Normalize business name for comparison
  const normalizeBusinessName = (name) => {
    if (!name) return '';
    return name.toLowerCase().replace(/\s+/g, ' ').trim();
  };

  const branchMatchesBusiness = (branch, businessKey) => {
    const branchBiz = normalizeBusinessName(branch.business_name);
    const searchBiz = normalizeBusinessName(businessKey);
    
    if (searchBiz.includes('holland')) {
      return branchBiz.includes('holland');
    }
    if (searchBiz.includes('paulo') || searchBiz.includes('cisneros')) {
      return branchBiz.includes('paulo') || branchBiz.includes('cisneros');
    }
    return branchBiz === searchBiz;
  };

  const getBusinessBranches = () => {
    if (!selectedBusiness) return [];
    return branches.filter(b => branchMatchesBusiness(b, selectedBusiness));
  };

  const startBooking = () => {
    if (!selectedBranch) {
      toast.error('Selecciona una sucursal');
      return;
    }
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.clientName.trim()) {
      toast.error('Ingresa tu nombre');
      return;
    }
    if (!formData.clientWhatsapp.trim()) {
      toast.error('Ingresa tu WhatsApp');
      return;
    }
    if (!formData.date) {
      toast.error('Selecciona una fecha');
      return;
    }
    if (!formData.time) {
      toast.error('Selecciona una hora');
      return;
    }
    if (!formData.barberId) {
      toast.error('Selecciona un barbero o estilista');
      return;
    }

    setIsLoading(true);
    try {
      const appointmentData = {
        branch_id: selectedBranch,
        barber_id: formData.barberId,
        service_id: formData.serviceId || null,
        client_name: formData.clientName.trim(),
        client_whatsapp: formData.clientWhatsapp.trim(),
        date: format(formData.date, 'yyyy-MM-dd'),
        time: formData.time,
        notes: formData.notes.trim() || null
      };

      const response = await axios.post(`${API}/appointments/public`, appointmentData);
      setAppointmentBooked(response.data);
      toast.success('¡Cita agendada exitosamente!');

      // Facebook Pixel: Schedule event with branch and brand info
      try {
        if (typeof window !== 'undefined' && typeof window.fbq === 'function') {
          const branchName = selectedBranchData?.name || '';
          const brandName = selectedBranchData?.business_name || businessConfig?.name || '';
          window.fbq('track', 'Schedule', {
            branch: branchName,
            brand: brandName,
          });
        }
      } catch (_fbErr) {
        // never block UX on pixel failures
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al agendar la cita');
    } finally {
      setIsLoading(false);
    }
  };

  const selectedBranchData = branches.find(b => b.id === selectedBranch);
  const businessConfig = selectedBusiness ? BUSINESSES[selectedBusiness] : null;
  const accentColor = businessConfig?.color === 'blue' ? 'blue-500' : 'gold';
  
  // Get time slots based on selected date and branch schedule
  const timeSlots = selectedBranchData && formData.date
    ? getTimeSlotsForDate(selectedBranchData, formData.date)
    : [];
  
  // Check if selected date is a closed day
  const isClosedDay = selectedBranchData && formData.date 
    ? !isBranchOpenOnDate(selectedBranchData, formData.date)
    : false;

  // ============== OVERLAY: MODO FESTIVO ==============
  if (holidayMode && holidayMode.enabled) {
    const endDateFormatted = holidayMode.end_date
      ? format(new Date(holidayMode.end_date + 'T12:00:00'), "d 'de' MMMM yyyy", { locale: es })
      : null;

    return (
      <div className="min-h-screen bg-[#050505] flex flex-col items-center justify-center px-4" data-testid="holiday-mode-overlay">
        <div className="max-w-md w-full text-center">
          <div className="w-20 h-20 mx-auto mb-6 bg-red-500/20 rounded-full flex items-center justify-center">
            <AlertTriangle className="w-10 h-10 text-red-500" />
          </div>
          <h1 className="font-heading text-3xl text-white mb-4">Días Festivos</h1>
          <p className="text-zinc-300 text-lg mb-2">
            {holidayMode.message || 'Disculpa pero estamos cerrados por días festivos.'}
          </p>
          {endDateFormatted && (
            <p className="text-zinc-400 mt-4">
              Abriremos de nuevo el <span className="text-white font-semibold">{endDateFormatted}</span>
            </p>
          )}
          <div className="mt-8 p-4 bg-zinc-900/80 border border-zinc-800 rounded-sm">
            <p className="text-zinc-500 text-sm">
              Agradecemos tu comprensión. Vuelve pronto.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // ============== PANTALLA 1: SELECCIÓN DE NEGOCIO ==============
  if (!selectedBusiness) {
    const hollandCount = branches.filter(b => branchMatchesBusiness(b, 'The Holland Barbershop')).length;
    const pauloCount = branches.filter(b => branchMatchesBusiness(b, 'Paulo Cisneros')).length;
    
    return (
      <div className="min-h-screen bg-[#050505] flex flex-col" data-testid="booking-landing">
        <header className="relative h-[30vh] min-h-[200px] flex items-center justify-center overflow-hidden">
          <div 
            className="absolute inset-0 bg-cover bg-center"
            style={{ backgroundImage: `url('https://images.unsplash.com/photo-1503951914875-452162b0f3f1?auto=format&fit=crop&q=80')` }}
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/50 to-[#050505]" />
          
          <div className="relative z-10 text-center px-4">
            <h1 className="font-heading text-3xl md:text-4xl text-white mb-2">Agenda tu Cita</h1>
            <p className="text-zinc-400">Selecciona el salón de tu preferencia</p>
          </div>
        </header>

        <main className="flex-1 flex items-center justify-center px-4 py-8 -mt-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-3xl w-full">
            {/* The Holland Barbershop */}
            <button
              onClick={() => selectBusiness('The Holland Barbershop')}
              className={`group bg-zinc-900/80 backdrop-blur-sm border border-zinc-800 rounded-sm p-8 hover:border-gold/50 transition-all duration-300 hover:scale-[1.02] ${hollandCount === 0 ? 'opacity-50 cursor-not-allowed' : ''}`}
              data-testid="select-holland-btn"
              disabled={hollandCount === 0}
            >
              <div className="flex flex-col items-center">
                <div className="w-32 h-32 mb-6 rounded-sm overflow-hidden bg-zinc-800 p-2 group-hover:bg-gold/10 transition-colors">
                  <img src={BUSINESSES['The Holland Barbershop'].logo} alt="The Holland Barbershop" className="w-full h-full object-contain" />
                </div>
                <h2 className="font-heading text-xl text-white mb-2 group-hover:text-gold transition-colors">The Holland Barbershop</h2>
                <p className="text-sm text-zinc-500">
                  {hollandCount === 0 ? 'Sin sucursales disponibles' : `${hollandCount} sucursal${hollandCount !== 1 ? 'es' : ''}`}
                </p>
              </div>
            </button>

            {/* Paulo Cisneros */}
            <button
              onClick={() => selectBusiness('Paulo Cisneros')}
              className={`group bg-zinc-900/80 backdrop-blur-sm border border-zinc-800 rounded-sm p-8 hover:border-blue-500/50 transition-all duration-300 hover:scale-[1.02] ${pauloCount === 0 ? 'opacity-50 cursor-not-allowed' : ''}`}
              data-testid="select-paulo-btn"
              disabled={pauloCount === 0}
            >
              <div className="flex flex-col items-center">
                <div className="w-32 h-32 mb-6 rounded-sm overflow-hidden bg-zinc-800 p-2 group-hover:bg-blue-500/10 transition-colors">
                  <img src={BUSINESSES['Paulo Cisneros'].logo} alt="Paulo Cisneros" className="w-full h-full object-contain" />
                </div>
                <h2 className="font-heading text-xl text-white mb-2 group-hover:text-blue-400 transition-colors">Paulo Cisneros</h2>
                <p className="text-sm text-zinc-500">
                  {pauloCount === 0 ? 'Sin sucursales disponibles' : `${pauloCount} sucursal${pauloCount !== 1 ? 'es' : ''}`}
                </p>
              </div>
            </button>
          </div>
        </main>

        <footer className="py-6 text-center border-t border-zinc-800">
          <p className="text-zinc-600 text-sm">Sistema de Citas Online</p>
        </footer>
      </div>
    );
  }

  // ============== PANTALLA 2: SUCURSAL Y FORMULARIO ==============
  return (
    <div className="min-h-screen bg-[#050505]" data-testid="booking-landing">
      {/* Hero Section */}
      <header className="relative h-[25vh] min-h-[180px] flex items-center justify-center overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url('https://images.unsplash.com/photo-1503951914875-452162b0f3f1?auto=format&fit=crop&q=80')` }}
        />
        <div className={`absolute inset-0 bg-gradient-to-b ${businessConfig?.bgGradient || 'from-black/70 via-black/50 to-[#050505]'}`} />
        
        <button
          onClick={goBackToBusinessSelection}
          className="absolute top-4 left-4 z-20 flex items-center gap-2 px-3 py-2 bg-black/50 hover:bg-black/70 rounded-sm text-zinc-400 hover:text-white transition-colors"
          data-testid="back-to-business-btn"
        >
          <ArrowLeft className="w-4 h-4" />
          <span className="text-sm">Cambiar salón</span>
        </button>
        
        <div className="relative z-10 text-center px-4">
          <div className="w-20 h-20 mx-auto mb-3 rounded-sm overflow-hidden bg-black/30 p-2">
            <img src={businessConfig?.logo} alt={businessConfig?.name} className="w-full h-full object-contain" />
          </div>
          <h1 className="font-heading text-2xl md:text-3xl text-white">{businessConfig?.name}</h1>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-lg mx-auto px-4 py-6 -mt-6 relative z-20">
        {/* Cita confirmada */}
        {appointmentBooked ? (
          <div className="bg-zinc-900/80 backdrop-blur-sm border border-green-500/30 rounded-sm p-6">
            <div className="text-center mb-6">
              <div className="w-16 h-16 mx-auto mb-4 bg-green-500/20 rounded-full flex items-center justify-center">
                <Check className="w-8 h-8 text-green-500" />
              </div>
              <h2 className="font-heading text-2xl text-white mb-2">¡Cita Confirmada!</h2>
              <p className="text-zinc-400">Te esperamos</p>
            </div>
            
            <div className="space-y-3 bg-zinc-800/50 rounded-sm p-4">
              <div className="flex items-center gap-3">
                <User className="w-5 h-5 text-zinc-500" />
                <span className="text-white">{appointmentBooked.client_name}</span>
              </div>
              <div className="flex items-center gap-3">
                <CalendarIcon className="w-5 h-5 text-zinc-500" />
                <span className="text-white">{appointmentBooked.date} a las {appointmentBooked.time}</span>
              </div>
              {appointmentBooked.service_name && (
                <div className="flex items-center gap-3">
                  <Scissors className="w-5 h-5 text-zinc-500" />
                  <span className="text-white">{appointmentBooked.service_name}</span>
                </div>
              )}
              <div className="flex items-center gap-3">
                <MapPin className="w-5 h-5 text-zinc-500" />
                <span className="text-white">{selectedBranchData?.name}</span>
              </div>
            </div>
            
            <Button
              onClick={() => { setAppointmentBooked(null); resetForm(); setShowForm(false); }}
              className={`w-full mt-6 ${accentColor === 'blue-500' ? 'bg-blue-500 hover:bg-blue-600' : 'bg-gold hover:bg-gold-dark'} text-black`}
            >
              Agendar otra cita
            </Button>
          </div>
        ) : !showForm ? (
          /* Selección de sucursal */
          <div className="bg-zinc-900/80 backdrop-blur-sm border border-zinc-800 rounded-sm p-6">
            <div className="text-center mb-6">
              <CalendarIcon className={`w-10 h-10 mx-auto mb-3 ${accentColor === 'blue-500' ? 'text-blue-500' : 'text-gold'}`} />
              <h2 className="font-heading text-xl text-white mb-1">Selecciona tu Sucursal</h2>
              <p className="text-sm text-zinc-500">Elige la más cercana a ti</p>
            </div>

            <div className="space-y-4">
              <Select value={selectedBranch} onValueChange={setSelectedBranch}>
                <SelectTrigger 
                  className={`h-12 bg-zinc-800 border-zinc-700 text-white ${selectedBranch ? (accentColor === 'blue-500' ? 'border-blue-500/50' : 'border-gold/50') : ''}`}
                  data-testid="branch-selector"
                >
                  <SelectValue placeholder="Selecciona una sucursal" />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-700 z-[100]">
                  {getBusinessBranches().map((branch) => (
                    <SelectItem key={branch.id} value={branch.id} className="text-white hover:bg-zinc-700">
                      <div className="flex items-center gap-2">
                        <MapPin className={`w-4 h-4 ${accentColor === 'blue-500' ? 'text-blue-500' : 'text-gold'}`} />
                        <span>{branch.name}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {selectedBranchData && (
                <div className={`p-3 bg-zinc-800/50 rounded-sm border ${accentColor === 'blue-500' ? 'border-blue-500/30' : 'border-gold/30'}`}>
                  <p className="text-sm text-zinc-400 flex items-center gap-2">
                    <MapPin className="w-4 h-4" />
                    {selectedBranchData.address}
                  </p>
                  <p className="text-sm text-zinc-500 mt-1 flex items-center gap-2">
                    <Clock className="w-4 h-4" />
                    {selectedBranchData.opening_time} - {selectedBranchData.closing_time}
                  </p>
                </div>
              )}

              <Button
                onClick={startBooking}
                disabled={!selectedBranch}
                className={`w-full h-12 font-semibold ${accentColor === 'blue-500' ? 'bg-blue-500 hover:bg-blue-600 text-white' : 'bg-gold hover:bg-gold-dark text-black'}`}
                data-testid="start-booking-btn"
              >
                <CalendarIcon className="w-5 h-5 mr-2" />
                Continuar
              </Button>
            </div>
          </div>
        ) : (
          /* Formulario de cita */
          <form onSubmit={handleSubmit} className="bg-zinc-900/80 backdrop-blur-sm border border-zinc-800 rounded-sm p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className={`font-heading text-xl ${accentColor === 'blue-500' ? 'text-blue-500' : 'text-gold'}`}>Nueva Cita</h2>
              <button
                type="button"
                onClick={() => setShowForm(false)}
                className="text-zinc-500 hover:text-white"
              >
                ✕
              </button>
            </div>

            <div className="space-y-4">
              {/* Nombre */}
              <div>
                <label className="flex items-center gap-2 text-sm text-zinc-400 mb-2">
                  <User className="w-4 h-4" />
                  Nombre del Cliente *
                </label>
                <Input
                  value={formData.clientName}
                  onChange={(e) => setFormData({...formData, clientName: e.target.value})}
                  placeholder="Nombre completo"
                  className="h-11 bg-zinc-800 border-zinc-700 text-white"
                  data-testid="client-name-input"
                />
              </div>

              {/* WhatsApp */}
              <div>
                <label className="flex items-center gap-2 text-sm text-zinc-400 mb-2">
                  <Phone className="w-4 h-4" />
                  WhatsApp *
                </label>
                <Input
                  value={formData.clientWhatsapp}
                  onChange={(e) => setFormData({...formData, clientWhatsapp: e.target.value})}
                  placeholder=""
                  className="h-11 bg-zinc-800 border-zinc-700 text-white"
                  data-testid="client-whatsapp-input"
                />
              </div>

              {/* Fecha */}
              <div>
                <label className="flex items-center gap-2 text-sm text-zinc-400 mb-2">
                  <CalendarIcon className="w-4 h-4" />
                  Fecha *
                </label>
                <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={`w-full h-11 justify-start text-left font-normal bg-zinc-800 border-zinc-700 text-white hover:bg-zinc-700 ${!formData.date && 'text-zinc-500'}`}
                      data-testid="date-picker-btn"
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {formData.date ? format(formData.date, 'PPP', { locale: es }) : 'Seleccionar fecha'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0 bg-zinc-900 border-zinc-700" align="start">
                    <Calendar
                      mode="single"
                      selected={formData.date}
                      onSelect={(date) => { 
                        // Clear time if date changes (might have different hours or be closed)
                        setFormData({...formData, date, time: ''}); 
                        setCalendarOpen(false); 
                      }}
                      disabled={(date) => {
                        const today = new Date();
                        today.setHours(0, 0, 0, 0);
                        return date < today;
                      }}
                      initialFocus
                      locale={es}
                      className="bg-zinc-900 text-white"
                    />
                  </PopoverContent>
                </Popover>
              </div>

              {/* Hora */}
              <div>
                <label className="flex items-center gap-2 text-sm text-zinc-400 mb-2">
                  <Clock className="w-4 h-4" />
                  Hora *
                </label>
                {isClosedDay ? (
                  <div className="h-11 bg-red-900/20 border border-red-900/50 rounded-md flex items-center justify-center text-red-400 text-sm">
                    Cerrado este día
                  </div>
                ) : (
                  <Select 
                    value={formData.time} 
                    onValueChange={(value) => setFormData({...formData, time: value})}
                    disabled={!formData.date || timeSlots.length === 0}
                  >
                    <SelectTrigger className="h-11 bg-zinc-800 border-zinc-700 text-white" data-testid="time-selector">
                      <SelectValue placeholder={
                        !formData.date 
                          ? "Primero selecciona fecha" 
                          : timeSlots.length === 0 
                            ? "Sin horarios disponibles" 
                            : "Seleccionar hora"
                      } />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-900 border-zinc-700 max-h-[200px]">
                      {timeSlots.map((slot) => {
                        const isBooked = formData.barberId && bookedSlots.includes(slot);
                        return (
                          <SelectItem 
                            key={slot} 
                            value={slot} 
                            disabled={isBooked}
                            className={`${isBooked ? 'text-zinc-600 line-through cursor-not-allowed' : 'text-white hover:bg-zinc-700'}`}
                          >
                            {slot} {isBooked && '(Ocupado)'}
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                )}
                {selectedBranchData && formData.date && !isClosedDay && (
                  <p className="text-xs text-zinc-500 mt-1">
                    Horario: {
                      formData.date.getDay() === 6 
                        ? `${selectedBranchData.saturday_opening_time || selectedBranchData.opening_time} - ${selectedBranchData.saturday_closing_time || selectedBranchData.closing_time}`
                        : formData.date.getDay() === 0
                          ? `${selectedBranchData.sunday_opening_time || selectedBranchData.opening_time} - ${selectedBranchData.sunday_closing_time || selectedBranchData.closing_time}`
                          : `${selectedBranchData.opening_time} - ${selectedBranchData.closing_time}`
                    }
                  </p>
                )}
              </div>

              {/* Barbero o Estilista */}
              <div>
                <label className="flex items-center gap-2 text-sm text-zinc-400 mb-2">
                  <Scissors className="w-4 h-4" />
                  Barbero o Estilista <span className="text-red-500">*</span>
                </label>
                <Select 
                  value={formData.barberId} 
                  onValueChange={(value) => setFormData({...formData, barberId: value})}
                >
                  <SelectTrigger className="h-11 bg-zinc-800 border-zinc-700 text-white" data-testid="barber-selector">
                    <SelectValue placeholder="Selecciona un barbero o estilista" />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-700">
                    {barbers.map((barber) => (
                      <SelectItem key={barber.id} value={barber.id} className="text-white hover:bg-zinc-700">
                        {barber.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Servicio (opcional) */}
              <div>
                <label className="flex items-center gap-2 text-sm text-zinc-400 mb-2">
                  <Scissors className="w-4 h-4" />
                  Servicio (opcional)
                </label>
                <Select 
                  value={formData.serviceId || "none"} 
                  onValueChange={(value) => setFormData({...formData, serviceId: value === "none" ? "" : value})}
                >
                  <SelectTrigger className="h-11 bg-zinc-800 border-zinc-700 text-white" data-testid="service-selector">
                    <SelectValue placeholder="Sin especificar" />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-700">
                    <SelectItem value="none" className="text-white hover:bg-zinc-700">Sin especificar</SelectItem>
                    {services.map((service) => (
                      <SelectItem key={service.id} value={service.id} className="text-white hover:bg-zinc-700">
                        {service.name} - ${service.price}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Notas (opcional) */}
              <div>
                <label className="flex items-center gap-2 text-sm text-zinc-400 mb-2">
                  <MessageSquare className="w-4 h-4" />
                  Notas (opcional)
                </label>
                <Textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({...formData, notes: e.target.value})}
                  placeholder="Notas adicionales..."
                  className="bg-zinc-800 border-zinc-700 text-white resize-none"
                  rows={2}
                  data-testid="notes-input"
                />
              </div>

              {/* Buttons */}
              <div className="flex gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowForm(false)}
                  className="flex-1 h-11 border-zinc-700 text-zinc-300 hover:bg-zinc-800"
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  disabled={isLoading}
                  className={`flex-1 h-11 font-semibold ${accentColor === 'blue-500' ? 'bg-blue-500 hover:bg-blue-600 text-white' : 'bg-gold hover:bg-gold-dark text-black'}`}
                  data-testid="submit-appointment-btn"
                >
                  {isLoading ? 'Agendando...' : '+ Agendar Cita'}
                </Button>
              </div>
            </div>
          </form>
        )}

        {/* Contact Info */}
        <div className="mt-6 flex flex-col items-center gap-3">
          <p className="text-zinc-500 text-sm">¿Prefieres escribirnos?</p>
          <a
            href="https://wa.me/527224656062"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="booking-whatsapp-button"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-emerald-500/50 text-emerald-300 bg-emerald-500/10 hover:bg-emerald-500/20 transition-colors text-sm font-medium"
          >
            <MessageCircle className="w-4 h-4" />
            722 465 6062
          </a>
        </div>
      </main>

      <footer className="py-6 text-center border-t border-zinc-800 mt-8">
        <p className="text-zinc-600 text-sm">© 2025 {businessConfig?.name}. Todos los derechos reservados.</p>
      </footer>
    </div>
  );
}
